/**
 * Static &lt;head&gt; markup for vite-prerender-plugin (React 19 + renderToString does not fill helmet context).
 */

import { getPublicLayoutSeo } from '../config/publicLayoutSeo'
import { absolutePublicUrl } from './publicSite'

const SITE_NAME = 'TCGEasy'
const DEFAULT_OG_IMAGE_PATH = '/images/og/tcgeasy-share.png'

function homeStructuredData(): string {
  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: SITE_NAME,
        url: absolutePublicUrl('/'),
        logo: absolutePublicUrl('/images/logo/tcgeasy-logo.png'),
      },
      { '@type': 'WebSite', name: SITE_NAME, url: absolutePublicUrl('/') },
    ],
  }
  return `<script type="application/ld+json">${JSON.stringify(data).replace(/</g, '\\u003c')}</script>`
}

function escapeHtmlAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function pageTitleWithBrand(title: string): string {
  const t = title.trim()
  if (t.toLowerCase().includes('tcgeasy')) return t
  return `${t} | ${SITE_NAME}`
}

export function buildPrerenderHeadMarkup(pathnameWithOptionalSearch: string): {
  title: string
  elements: Set<string>
} {
  const pathnameOnly = pathnameWithOptionalSearch.split('?')[0] || '/'
  const seo = getPublicLayoutSeo(pathnameOnly)
  const pageTitle = pageTitleWithBrand(seo.title)
  const description = seo.description
  const canonicalUrl = absolutePublicUrl(pathnameOnly)
  const ogImagePath = seo.ogImagePath ?? DEFAULT_OG_IMAGE_PATH
  const imageUrl = absolutePublicUrl(ogImagePath)

  const d = escapeHtmlAttr(description)
  const t = escapeHtmlAttr(pageTitle)
  const u = escapeHtmlAttr(canonicalUrl)
  const img = escapeHtmlAttr(imageUrl)
  const site = escapeHtmlAttr(SITE_NAME)

  const elements = new Set([
    `<meta name="description" content="${d}">`,
    `<link rel="canonical" href="${u}">`,
    `<meta property="og:title" content="${t}">`,
    `<meta property="og:description" content="${d}">`,
    `<meta property="og:url" content="${u}">`,
    `<meta property="og:image" content="${img}">`,
    `<meta property="og:image:width" content="1200">`,
    `<meta property="og:image:height" content="630">`,
    `<meta property="og:type" content="website">`,
    `<meta property="og:site_name" content="${site}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${t}">`,
    `<meta name="twitter:description" content="${d}">`,
    `<meta name="twitter:image" content="${img}">`,
  ])
  if (pathnameOnly === '/') elements.add(homeStructuredData())
  if (seo.noIndex) elements.add('<meta name="robots" content="noindex">')

  return { title: pageTitle, elements }
}
